import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  aplicarDevolucionAlPlan, cancelarPlan, ESTADOS_DE_MENSUALIDAD, ESTADOS_DE_PLAN,
  liquidarMensualidades, obtenerPlan,
} from '../api/msiApi.js';
import { CalendarioPlegadoPorAnio } from '../componentes/CalendarioPlegadoPorAnio.jsx';
import { RecordatorioDePago } from '../componentes/RecordatorioDePago.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import {
  importe as formatearImporte, LEYENDA_PLAN_CERRADO_POR_BAJA, LEYENDA_REPARTO_DE_DEUDA,
  NOMBRE_DE_ESTADO_DE_MENSUALIDAD, NOMBRE_DE_ESTADO_DE_PLAN,
} from '../formato.js';

/**
 * Los renglones que el pliegue necesita, armados con lo que YA vino resuelto del
 * servidor (RNF-038): aquí no se concatena ningún contador, no se divide ningún
 * importe y no se calcula ninguna fecha.
 *
 * «La que sigue» es la PRIMERA pendiente de la lista que el servidor ya devolvió en
 * orden; «cargada» es todo lo que no está pendiente —cargada por fecha, liquidada o
 * cargada histórica—, que es lo que RN-772 manda contar junto en el resumen del
 * año. La distinción de la histórica no se pierde: vive en su renglón.
 */
function renglonesDelCalendario(mensualidades) {
  const laQueSigue = mensualidades.find(
    (mensualidad) => mensualidad.estadoPresentado === ESTADOS_DE_MENSUALIDAD.pendiente,
  );

  return mensualidades.map((mensualidad) => ({
    clave: mensualidad.idMensualidad,
    fechaCorte: mensualidad.fechaCorte,
    importe: mensualidad.importe,
    estaCargada: mensualidad.estadoPresentado !== ESTADOS_DE_MENSUALIDAD.pendiente,
    esHistorica: Boolean(mensualidad.esHistorica),
    esLaQueSigue: laQueSigue?.idMensualidad === mensualidad.idMensualidad,
    esLaDelResiduo: Boolean(mensualidad.esLaQueAbsorbeElResiduo),
    origen: mensualidad,
  }));
}

/**
 * Panel del plan (RF-069) y sus tres operaciones: liquidar por anticipado
 * (RF-071), aplicar una devolución (RF-072) y cancelar el plan (RN-108c).
 *
 * DECISIONES QUE ESTA PANTALLA HACE CUMPLIR, y ninguna se decide aquí:
 *   · EL SALDO DEL PLAN ES UN REPARTO DE DEUDA YA CONTADA, NO DEUDA ADICIONAL
 *     (D-53, RN-103c). Se etiqueta como parte de la deuda ya mostrada y no se suma
 *     junto a ella. Aquí NO SE MUESTRA NINGÚN SALDO NI NINGUNA DEUDA de la tarjeta:
 *     esos los da Cuentas.
 *   · LIQUIDAR NO MUEVE DINERO Y NO TOCA LA DEUDA (D-60, RN-110b): el pago se
 *     registra APARTE con el 3/6, y esta pantalla lo ofrece con el importe ya
 *     puesto. Si liquidar bajara la deuda además del pago, la deuda bajaría dos
 *     veces por el mismo dinero.
 *   · El estado de cada mensualidad, el contador y los importes LLEGAN RESUELTOS
 *     del servidor. La pantalla no calcula fechas, no divide importes y no
 *     concatena contadores (RNF-035, RNF-038).
 *   · En un plan AJENO las tres acciones se ven DESHABILITADAS CON LEYENDA. Eso es
 *     ayuda visual: el servidor responde 403 igual si se fuerza la llamada
 *     (RNF-018, RN-112).
 */
export default function PanelDelPlan() {
  const { idPlan } = useParams();
  const hoy = obtenerFechaDeNegocioHoy();

  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [dialogo, setDialogo] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [idDevolucion, setIdDevolucion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ultimaLiquidacion, setUltimaLiquidacion] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      setPlan(await obtenerPlan(idPlan, hoy));
    } catch (fallo) {
      setError(fallo);
    }
  }, [idPlan, hoy]);

  useEffect(() => { cargar(); }, [cargar]);

  async function ejecutar(accion) {
    setError(null);
    setAviso(null);
    try {
      if (accion === 'liquidar') {
        const resultado = await liquidarMensualidades(idPlan, {
          cantidadDeMensualidades: Number(cantidad),
          fechaLocal: hoy,
          confirmado: true,
          version: plan.version,
        });
        setUltimaLiquidacion(resultado);
        setPlan(resultado.plan);
      } else if (accion === 'devolucion') {
        const resultado = await aplicarDevolucionAlPlan(idPlan, {
          idMovimientoDevolucion: Number(idDevolucion),
          fechaLocal: hoy,
          version: plan.version,
        });
        setPlan(resultado.plan);
      } else if (accion === 'cancelar') {
        setPlan(await cancelarPlan(idPlan, {
          fechaLocal: hoy, confirmado: true, motivo: motivo || null, version: plan.version,
        }));
      }
      setDialogo(null);
    } catch (fallo) {
      // EL TEXTO DE LA CONFIRMACIÓN LO ESCRIBE EL SERVIDOR, no la pantalla: así la
      // salvaguarda no depende de que la interfaz se acuerde de pedirla (RN-110e).
      if (fallo.codigo === 'CONFIRMACION_REQUERIDA') setDialogo({ accion, texto: fallo.mensaje });
      else setError(fallo);
    }
  }

  async function pedirConfirmacion(accion) {
    setError(null);
    try {
      if (accion === 'liquidar') {
        await liquidarMensualidades(idPlan, {
          cantidadDeMensualidades: Number(cantidad), fechaLocal: hoy, confirmado: false, version: plan.version,
        });
      } else if (accion === 'cancelar') {
        await cancelarPlan(idPlan, { fechaLocal: hoy, confirmado: false, version: plan.version });
      } else {
        // Aplicar una devolución NO pide confirmación: no destruye nada y es
        // reversible cancelando el plan.
        await ejecutar('devolucion');
      }
    } catch (fallo) {
      if (fallo.codigo === 'CONFIRMACION_REQUERIDA') setDialogo({ accion, texto: fallo.mensaje });
      else setError(fallo);
    }
  }

  if (error && !plan) {
    return (
      <Shell titulo="Plan de meses" volverA="/cuentas/meses">
        <AvisoDeError error={error} />
      </Shell>
    );
  }

  if (!plan) {
    return (
      <Shell titulo="Plan de meses" volverA="/cuentas/meses">
        <div className="card" style={{ margin: 12 }}>Cargando…</div>
      </Shell>
    );
  }

  const cerradoPorBaja = plan.claveEstado === ESTADOS_DE_PLAN.cerradoPorBaja;
  const leyendaAjeno = plan.esPropio
    ? null
    : `Este plan es de ${plan.nombrePropietario}. Puedes verlo, pero solo esa persona puede operarlo.`;

  return (
    <Shell titulo="Plan de meses" volverA="/cuentas/meses">
      <AvisoDeError error={error} />
      {aviso && <div className="aviso i">{aviso}</div>}

      <div className="card" style={{ margin: 12 }}>
        <div className="fila">
          <span className="crece">Compra</span>
          <b>{plan.descripcionDelGasto || 'Compra a meses'}</b>
        </div>
        <div className="fila"><span className="crece">Fecha</span><b>{formatearFecha(plan.fechaDelGasto)}</b></div>
        <div className="fila">
          <span className="crece">Tarjeta</span>
          <b>{plan.nombreTarjeta}{plan.cuatroUltimosDigitos ? ` ····${plan.cuatroUltimosDigitos}` : ''}</b>
        </div>
        <div className="fila"><span className="crece">Propietario</span><b>{plan.nombrePropietario}</b></div>
        <div className="fila">
          <span className="crece">Importe de la compra</span>
          <b>{formatearImporte(plan.importeOriginal, plan.codigoISO)}</b>
        </div>
        <div className="fila">
          <span className="crece">Plan</span>
          <b>{plan.numeroDeMeses} meses</b>
        </div>
        <div className="fila">
          <span className="crece">Estado</span>
          <b data-prueba="estado-del-plan">
            {NOMBRE_DE_ESTADO_DE_PLAN[plan.claveEstado] ?? plan.nombreEstado}
          </b>
        </div>
      </div>

      {cerradoPorBaja && (
        <div className="aviso i" data-prueba="plan-cerrado-por-baja">
          {/* Fila 23 del inventario del incremento 15 (RF-222): salió la justificación de la
              implementación («nunca se escribió ninguno»). */}
          <b>{LEYENDA_PLAN_CERRADO_POR_BAJA}</b> Es de solo consulta. <b>Si reactivas la tarjeta, el
          plan vuelve a estar vigente con sus mensualidades y sus fechas intactas</b>.
        </div>
      )}

      {/* RN-111a: aviso informativo de que la tarjeta cambió de fechas después de
          generarse el plan. NO BLOQUEA NADA y NO HAY RECÁLCULO: el calendario del
          plan se fijó al generarlo (RN-101e). */}
      {plan.avisoCambioDeFechasDeTarjeta && (
        <div className="aviso i" data-prueba="aviso-cambio-de-fechas">
          Tu tarjeta cambió de día de corte o de día límite <b>después</b> de generarse este plan.
          <b> Las mensualidades ya calculadas no se recalculan</b>: se quedan con las fechas que tenían.
        </div>
      )}

      {/* D-74 / RN-124: RENGLÓN INFORMATIVO, del mismo peso visual que el aviso de
          arriba. No bloquea, no altera ningún saldo y NO TIENE BOTÓN DE DESCARTAR. */}
      {/* La liquidación devuelve el recordatorio YA POBLADO en su propia respuesta
          (RN-124, prueba 51): se toma de ahí si acaba de ocurrir, y del plan al
          volver a entrar. LA CIFRA ES LA MISMA en los dos casos porque la calcula
          el servidor por tarjeta (RN-124c). */}
      <RecordatorioDePago
        recordatorio={
          ultimaLiquidacion?.recordatorioDePagoDeLiquidacion
          ?? plan.recordatorioDePagoDeLiquidacion
        }
        idCuenta={plan.idCuenta}
        esPropio={plan.esPropio}
      />

      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>SALDO DEL PLAN</h4>
        <div className="fila">
          <span className="crece">Falta por caer en cortes futuros</span>
          <b data-prueba="saldo-del-plan">{formatearImporte(plan.saldoDelPlan, plan.codigoISO)}</b>
        </div>
        <div className="fila">
          <span className="crece">Ya cargado</span>
          <b>{formatearImporte(plan.totalCargado, plan.codigoISO)}</b>
        </div>
        <div className="fila">
          <span className="crece">Liquidado por anticipado</span>
          <b>{formatearImporte(plan.totalLiquidado, plan.codigoISO)}</b>
        </div>
        <div className="fila">
          <span className="crece">Devoluciones aplicadas</span>
          <b>{formatearImporte(plan.totalDevueltoAplicado, plan.codigoISO)}</b>
        </div>
        {/* REGLA, NO ESTILO (D-53, RN-103c). */}
        <span className="ayuda" data-prueba="leyenda-reparto-de-deuda">{LEYENDA_REPARTO_DE_DEUDA}</span>
      </div>

      <div className="card" style={{ margin: 12 }} data-prueba="mensualidades-del-plan">
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>MENSUALIDADES</h4>

        {/* PLIEGUE POR AÑO EN LOS PLANES LARGOS (RF-274, RN-767). Con 24
            mensualidades o menos esto pinta exactamente la misma lista de antes;
            arriba de 24 agrupa por año y abre solo el de la que sigue. El renglón
            NO CAMBIA: es el mismo de siempre, dentro del año abierto. */}
        <CalendarioPlegadoPorAnio
          renglones={renglonesDelCalendario(plan.mensualidades)}
          codigoISO={plan.codigoISO}
          datoDePrueba="mensualidades-del-plan-lista"
          pintarRenglon={(renglon) => {
            const mensualidad = renglon.origen;

            return (
              <div className="fila" key={mensualidad.idMensualidad}>
                <div className="crece">
                  <b data-prueba="contador">{mensualidad.contadorDeMensualidad}</b>
                  <span className="chip">{NOMBRE_DE_ESTADO_DE_MENSUALIDAD[mensualidad.estadoPresentado]}</span>
                  <small>
                    corte {formatearFecha(mensualidad.fechaCorte)} · pago a más tardar el{' '}
                    {formatearFecha(mensualidad.fechaLimitePago)}
                  </small>
                </div>
                <span className="imp">
                  {formatearImporte(mensualidad.importe, plan.codigoISO)}
                  {mensualidad.esLaQueAbsorbeElResiduo && (
                    <small data-prueba="absorbe-residuo">lleva los centavos que sobran</small>
                  )}
                </span>
              </div>
            );
          }}
        />
      </div>

      {/* TRAS LIQUIDAR, la pantalla ofrece el pago con el importe YA PUESTO y
          advierte que se registra aparte (RN-110b, RN-110f, prueba 51). */}
      {ultimaLiquidacion && (
        <div className="aviso i" data-prueba="tras-liquidar">
          Liquidaste por <b>{formatearImporte(ultimaLiquidacion.importeLiquidado, plan.codigoISO)}</b>.
          <b> El pago a la tarjeta se registra aparte</b>: esta operación no movió dinero y no cambió
          tu deuda.
          <div style={{ marginTop: 8 }}>
            <Link
              className="btn sec"
              to={`/movimientos/traspaso?idCuentaDestino=${plan.idCuenta}&importe=${ultimaLiquidacion.importeLiquidado}`}
            >
              Registrar el pago de la tarjeta
            </Link>
          </div>
        </div>
      )}

      {dialogo && (
        <div className="card" style={{ margin: 12 }} data-prueba="confirmacion">
          <p>{dialogo.texto}</p>
          <button className="btn ancho" type="button" onClick={() => ejecutar(dialogo.accion)}>
            Sí, continuar
          </button>
          <button className="btn sec ancho" type="button" onClick={() => setDialogo(null)}>
            No
          </button>
        </div>
      )}

      <div className="card" style={{ margin: 12 }} data-prueba="acciones-del-plan">
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>OPERAR ESTE PLAN</h4>

        <div className="campo">
          <label htmlFor="cantidad">LIQUIDAR POR ANTICIPADO (MENSUALIDADES)</label>
          <input id="cantidad" name="cantidad" type="number" min={1} value={cantidad}
            disabled={!plan.puedeLiquidarse}
            onChange={(evento) => setCantidad(evento.target.value)} />
          <span className="ayuda">
            Se saldan <b>desde la más lejana hacia atrás</b>, así el plan termina antes.
            <b> No mueve dinero</b>: el pago se registra aparte.
          </span>
        </div>

        <button className="btn ancho" type="button" name="liquidar"
          disabled={!plan.puedeLiquidarse} onClick={() => pedirConfirmacion('liquidar')}>
          Liquidar por anticipado
        </button>

        <div className="campo">
          <label htmlFor="idDevolucion">APLICAR UNA DEVOLUCIÓN YA REGISTRADA</label>
          <input id="idDevolucion" name="idDevolucion" type="number" value={idDevolucion}
            disabled={!plan.puedeAplicarseDevolucion}
            onChange={(evento) => setIdDevolucion(evento.target.value)} />
          {/* Fila 24 del inventario del incremento 15 (RF-222): salió la justificación del
              diseño («el sistema no lo adivina…»). */}
          <span className="ayuda" data-prueba="plan-aplicar-devolucion-ayuda">
            Primero regístrala como <b>Devolución</b> con el movimiento; aquí le dices <b>a qué compra
            corresponde</b>.
          </span>
        </div>

        <button className="btn sec ancho" type="button" name="devolucion"
          disabled={!plan.puedeAplicarseDevolucion} onClick={() => pedirConfirmacion('devolucion')}>
          Aplicar la devolución a este plan
        </button>

        <div className="campo">
          <label htmlFor="motivo">MOTIVO DE LA CANCELACIÓN (OPCIONAL)</label>
          <input id="motivo" name="motivo" type="text" maxLength={500} value={motivo}
            disabled={!plan.puedeCancelarse}
            onChange={(evento) => setMotivo(evento.target.value)} />
        </div>

        <button className="btn sec ancho" type="button" name="cancelar"
          disabled={!plan.puedeCancelarse} onClick={() => pedirConfirmacion('cancelar')}>
          Cancelar el plan
        </button>

        <span className="ayuda">
          Cancelar el plan <b>no cancela el gasto</b>: sigue registrado y sigue en la deuda. Es la
          salida para corregir el importe, la fecha o la tarjeta.
        </span>

        {leyendaAjeno && (
          <span className="ayuda" data-prueba="leyenda-ajeno">{leyendaAjeno}</span>
        )}
      </div>

      {plan.ajustes.length > 0 && (
        <div className="card lista" style={{ margin: 12 }} data-prueba="historial-de-ajustes">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>HISTORIAL DEL PLAN</h4>
          {plan.ajustes.map((ajuste) => (
            <div className="fila" key={ajuste.idAjusteDePlan}>
              <div className="crece">
                <b>{ajuste.nombreTipo}</b>
                <small>
                  {formatearFecha(ajuste.fecha)}
                  {ajuste.cantidadDeMensualidades > 0 && ` · ${ajuste.cantidadDeMensualidades} mensualidad(es)`}
                  {ajuste.nota && ` · ${ajuste.nota}`}
                </small>
              </div>
              <span className="imp">{formatearImporte(ajuste.importeAplicado, plan.codigoISO)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ margin: '10px 12px' }}>
        <Link className="btn sec ancho" to={`/movimientos/${plan.idMovimiento}`}>
          Ver el gasto completo ({formatearImporte(plan.importeOriginal, plan.codigoISO)})
        </Link>
        <span className="ayuda">
          El detalle del gasto <b>nunca se reparte</b>: ahí el importe son siempre los de la compra,
          porque es el hecho, y es donde se edita, se cancela y se borra.
        </span>
      </div>

      <div hidden>{ESTADOS_DE_MENSUALIDAD.pendiente}</div>
    </Shell>
  );
}
